import {
  CheckmarkFilled,
  CircleDash,
  ErrorFilled,
  Information,
} from '@carbon/react/icons';
import { InlineLoading } from '@carbon/react';
import { useCallback } from 'react';
import { TooltipIcon } from 'components/tooltip-icon/tooltip-icon';
import type { StatusItem } from 'pages/crm-tracts-subtracts/types';
import style from './status-checklist.module.scss';

interface StatusChecklistProps {
  statusItems: StatusItem[];
  /**
   * When true, each row shows a per-status icon next to its value and the row
   * uses the roomier "panel" layout (the Aries MDB export modal's progress /
   * failed views — matching the v6 mock). Left OFF by the
   * `WorkflowStatusPopover`, which keeps its compact text-only rows so its
   * existing appearance is unchanged.
   */
  showStatusIcon?: boolean;
}

/**
 * Maps a checklist row to its icon. Keyed on the raw workflow `status` when the
 * item carries one — so rewording a display label can't silently drop icons —
 * with a case-insensitive fallback to the display `value` for consumers that
 * only set it. Rows matching no known status (display-only / informational
 * rows) render no icon.
 *
 * @param {StatusItem} item - The row's status item.
 * @return {JSX.Element | null} The status icon, or null when none applies.
 */
const StatusIcon = ({ item }: { item: StatusItem }): JSX.Element | null => {
  switch ((item.status ?? item.value).toLowerCase()) {
    case 'processed':
    case 'completed':
      return <CheckmarkFilled className={style.iconCompleted} />;
    case 'failed':
      return <ErrorFilled className={style.iconFailed} />;
    case 'processing':
    case 'in progress':
      return <InlineLoading status="active" className={style.iconInProgress} />;
    case 'pending':
    case 'blocked':
    case 'unnecessary':
      // Not run: still to come, stopped by an earlier failure, or nothing to do.
      return <CircleDash className={style.iconPending} />;
    default:
      return null;
  }
};

/**
 * A single checklist row. In the panel variant (`showStatusIcon` — the modal's
 * progress / failed views) the status icon LEADS the row: icon, then label, then
 * the status word on the right, matching the v6 mock. The popover variant (no
 * icon) is just label on the left, value on the right. The click/mousedown guards
 * stop the event bubbling to the popover's outside-click handler (which would
 * close it); they are harmless inside the modal.
 *
 * @param {object} props - The status item and whether to show its icon.
 * @return {JSX.Element} The row.
 */
const StatusItemRow = ({
  status,
  showStatusIcon,
}: {
  status: StatusItem;
  showStatusIcon: boolean;
}): JSX.Element => {
  const stop = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <div
      className={
        showStatusIcon
          ? `${style.statusItem} ${style.statusItemPanel}`
          : style.statusItem
      }
      onMouseDown={stop}
      onClick={stop}
    >
      {showStatusIcon && (
        <span className={style.statusIconLeading}>
          <StatusIcon item={status} />
        </span>
      )}
      <span className={style.statusLabel}>{status.label}</span>
      <span className={style.statusValue}>
        {status.value}
        {status.details && (
          <TooltipIcon
            definition={status.details}
            align="top"
            autoAlign
            onClick={stop}
            onMouseDown={stop}
          >
            <Information className={style.statusInfoIcon} />
          </TooltipIcon>
        )}
      </span>
    </div>
  );
};

/**
 * The shared, presentational status checklist — one row per `StatusItem`. Used
 * by BOTH the `WorkflowStatusPopover` (text-only rows) and the Aries MDB export
 * modal's progress / failed views (`showStatusIcon`), so the row structure and
 * status→icon mapping live in exactly one place and can't drift.
 *
 * @param {StatusChecklistProps} props - The rows and the icon toggle.
 * @return {JSX.Element} The rendered rows (no wrapping element; the consumer
 *   supplies the container).
 */
export const StatusChecklist = ({
  statusItems,
  showStatusIcon = false,
}: StatusChecklistProps): JSX.Element => (
  <>
    {statusItems?.map((status, index) => (
      <StatusItemRow
        key={status.name || index}
        status={status}
        showStatusIcon={showStatusIcon}
      />
    ))}
  </>
);
